import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { ModalContainer } from '@components/Modals/ModalContainer'
import { Box, Button, Stack, Text } from '@ui'
import type {
    MiniappSignatureMethod,
    MiniappSignatureParams,
} from 'hooks/useMiniappEthereumProvider'
import { shortAddress, shortenHexString } from 'ui/utils/utils'
import { hexToString } from 'viem'
import { z } from 'zod'

export type MiniappSignaturePrompt = {
    method: MiniappSignatureMethod
    fromAddress: string
    params: MiniappSignatureParams
}

type Props = {
    prompt: MiniappSignaturePrompt | null
    onApprove: () => void
    onReject: () => void
}

type SignaturePromptResolver = {
    resolve: () => void
    reject: (reason?: unknown) => void
}

type SignaturePromptRequest = MiniappSignaturePrompt

export const useMiniappSignaturePrompt = () => {
    const [prompt, setPrompt] = useState<MiniappSignaturePrompt | null>(null)
    const resolverRef = useRef<SignaturePromptResolver | null>(null)

    const requestSignatureApproval = useCallback(
        ({ method, params, fromAddress }: SignaturePromptRequest) => {
            return new Promise<void>((resolve, reject) => {
                if (!fromAddress) {
                    reject(new Error('Missing signer address for signature request'))
                    return
                }

                // Reject new requests if one is already pending to prevent promise leaks.
                // The caller should wait for the current request to complete.
                if (resolverRef.current) {
                    reject(new Error('Another signature request is pending'))
                    return
                }

                resolverRef.current = { resolve, reject }
                setPrompt({
                    method,
                    params,
                    fromAddress,
                })
            })
        },
        [],
    )

    const resolveSignaturePrompt = useCallback(() => {
        resolverRef.current?.resolve()
        resolverRef.current = null
        setPrompt(null)
    }, [])

    const rejectSignaturePrompt = useCallback((reason?: Error) => {
        resolverRef.current?.reject(reason ?? new Error('User rejected signature'))
        resolverRef.current = null
        setPrompt(null)
    }, [])

    const handleSignatureReject = useCallback(() => {
        rejectSignaturePrompt(new Error('User rejected signature'))
    }, [rejectSignaturePrompt])

    useEffect(() => {
        return () => {
            if (resolverRef.current) {
                resolverRef.current.reject(new Error('Miniapp closed'))
                resolverRef.current = null
            }
        }
    }, [])

    return {
        prompt,
        requestSignatureApproval,
        resolveSignaturePrompt,
        rejectSignaturePrompt,
        handleSignatureReject,
    }
}

const formatMessage = (value: unknown) => {
    if (typeof value !== 'string') {
        return undefined
    }
    if (value.startsWith('0x')) {
        try {
            return hexToString(value as `0x${string}`)
        } catch {
            return value
        }
    }
    return value
}

type ParsedTypedData = {
    primaryType?: string
    domain?: Record<string, unknown>
    message?: Record<string, unknown>
    types?: Record<string, TypedDataTypeDefinition[]>
}

const typedDataFieldSchema = z.object({
    name: z.string(),
    type: z.string(),
})

type TypedDataTypeDefinition = z.infer<typeof typedDataFieldSchema>

const typedDataSchema = z.object({
    primaryType: z.string().optional(),
    domain: z.record(z.unknown()).optional(),
    message: z.record(z.unknown()).optional(),
    types: z.record(z.array(typedDataFieldSchema)).optional(),
})

// Parses EIP-712 typed data payloads so the modal can highlight important sections.
const parseTypedData = (value: unknown): ParsedTypedData | undefined => {
    if (typeof value !== 'string') {
        return undefined
    }
    try {
        const parsedJson = JSON.parse(value)
        const parsed = typedDataSchema.safeParse(parsedJson)
        if (!parsed.success) {
            return undefined
        }

        const normalizedTypes = parsed.data.types
            ? (() => {
                  const entries = Object.entries(parsed.data.types).reduce<
                      Record<string, TypedDataTypeDefinition[]>
                  >((acc, [key, definitions]) => {
                      if (definitions.length > 0) {
                          acc[key] = definitions
                      }
                      return acc
                  }, {})
                  return Object.keys(entries).length > 0 ? entries : undefined
              })()
            : undefined

        return {
            domain: parsed.data.domain,
            message: parsed.data.message,
            types: normalizedTypes,
            primaryType: parsed.data.primaryType,
        }
    } catch {
        return undefined
    }
}

// Displays primitive values inline and complex structures as formatted JSON blocks.
type TypedDataRow = {
    name: string
    type?: string
    value: unknown
}

const buildTypedDataRows = (
    values?: Record<string, unknown>,
    definitions?: TypedDataTypeDefinition[],
): TypedDataRow[] => {
    if (!values) {
        return []
    }

    const definitionMap = new Map<string, string>()
    definitions?.forEach((definition) => {
        definitionMap.set(definition.name, definition.type)
    })

    return Object.entries(values).map(([name, value]) => ({
        name,
        type: definitionMap.get(name),
        value,
    }))
}

const formatScalarValue = (value: string, type?: string) => {
    if (type === 'address') {
        return shortAddress(value)
    }
    if (value.startsWith('0x')) {
        return shortenHexString(value)
    }
    return value
}

const StructuredValue = ({ value, type }: { value: unknown; type?: string }) => {
    if (
        value === null ||
        typeof value === 'string' ||
        typeof value === 'number' ||
        typeof value === 'boolean'
    ) {
        const displayValue =
            typeof value === 'string' ? formatScalarValue(value, type) : String(value)
        return <Text>{displayValue}</Text>
    }

    return (
        <Box as="pre" whiteSpace="preWrap">
            {JSON.stringify(value, null, 2)}
        </Box>
    )
}

const getFieldLabel = (row: TypedDataRow) => {
    return row.type ? `${row.name} (${row.type})` : row.name
}

const TypedDataSection = ({ title, rows }: { title: string; rows: TypedDataRow[] }) => {
    if (rows.length === 0) {
        return null
    }

    return (
        <Stack gap="xs">
            <Text fontWeight="medium">{title}</Text>
            <Stack gap="sm" background="level1" padding="md" borderRadius="md">
                {rows.map((row) => (
                    <Stack
                        horizontal
                        key={`${row.name}-${row.type ?? 'value'}`}
                        justifyContent="spaceBetween"
                        alignItems="start"
                        gap="md"
                    >
                        <Text color="gray2">{getFieldLabel(row)}</Text>
                        <Box
                            textAlign="right"
                            style={{ maxWidth: '60%', overflowWrap: 'anywhere' }}
                        >
                            <StructuredValue value={row.value} type={row.type} />
                        </Box>
                    </Stack>
                ))}
            </Stack>
        </Stack>
    )
}

// Combines the typed-data sections into a single card with contextual headings.
const TypedDataDetails = ({ typedData }: { typedData: ParsedTypedData }) => {
    const domainRows = buildTypedDataRows(typedData.domain, typedData.types?.EIP712Domain)
    const messageDefinitions = typedData.primaryType
        ? typedData.types?.[typedData.primaryType]
        : undefined
    const messageRows = buildTypedDataRows(typedData.message, messageDefinitions)
    const messageTitle = typedData.primaryType
        ? `${typedData.primaryType} fields`
        : 'Message Fields'

    return (
        <Stack gap>
            <Text fontWeight="medium">Typed Data</Text>
            {typedData.primaryType && (
                <Stack background="level1" padding="md" borderRadius="md">
                    <Stack horizontal justifyContent="spaceBetween" alignItems="center">
                        <Text color="gray2">Primary Type</Text>
                        <Text>{typedData.primaryType}</Text>
                    </Stack>
                </Stack>
            )}

            <TypedDataSection title="Domain Fields" rows={domainRows} />
            <TypedDataSection title={messageTitle} rows={messageRows} />
        </Stack>
    )
}

export const MiniappSignatureModal = ({ prompt, onApprove, onReject }: Props) => {
    const paramsArray = useMemo(() => {
        return Array.isArray(prompt?.params) ? (prompt?.params as unknown[]) : []
    }, [prompt?.params])

    const { message, typedData } = useMemo(() => {
        if (!prompt) {
            return { message: undefined, typedData: undefined }
        }
        if (prompt.method === 'personal_sign') {
            const raw = paramsArray[0]
            return { message: formatMessage(raw), typedData: undefined }
        }
        if (prompt.method === 'eth_sign') {
            const raw = paramsArray[1]
            return { message: formatMessage(raw), typedData: undefined }
        }
        if (prompt.method === 'eth_signTypedData' || prompt.method === 'eth_signTypedData_v4') {
            const raw = paramsArray[1]
            return { message: undefined, typedData: parseTypedData(raw) }
        }
        return { message: undefined, typedData: undefined }
    }, [paramsArray, prompt])

    if (!prompt) {
        return null
    }

    return (
        <ModalContainer
            disableBackgroundClick
            touchTitle="Approve signature"
            padding="lg"
            onHide={onReject}
        >
            <Stack gap="lg">
                <Stack gap="sm">
                    <Text fontWeight="strong">Miniapp requests a signature</Text>
                    <Text color="gray2" fontSize="sm">
                        Verify the payload below before approving. Signature will be made by
                        <br />
                        {shortAddress(prompt.fromAddress)}.
                    </Text>
                </Stack>

                <Stack gap background="level1" padding="md" borderRadius="md">
                    <Stack horizontal justifyContent="spaceBetween">
                        <Text color="gray2">From</Text>
                        <Text>{shortAddress(prompt.fromAddress)}</Text>
                    </Stack>
                    <Stack horizontal justifyContent="spaceBetween">
                        <Text color="gray2">Method</Text>
                        <Text>{prompt.method}</Text>
                    </Stack>
                </Stack>

                {message && (
                    <Stack gap background="level1" padding="md" borderRadius="md">
                        <Text fontWeight="medium">Message</Text>
                        <Text whiteSpace="preWrap">{message}</Text>
                    </Stack>
                )}

                {typedData && <TypedDataDetails typedData={typedData} />}

                {!message && !typedData && (
                    <Stack gap background="level1" padding="md" borderRadius="md">
                        <Text fontWeight="medium">Raw Parameters</Text>
                        <Box as="pre" whiteSpace="preWrap">
                            {JSON.stringify(paramsArray, null, 2)}
                        </Box>
                    </Stack>
                )}

                <Stack gap horizontal justifyContent="end">
                    <Button tone="transparent5" onClick={onReject}>
                        Cancel
                    </Button>
                    <Button tone="cta1" onClick={onApprove}>
                        Approve &amp; Sign
                    </Button>
                </Stack>
            </Stack>
        </ModalContainer>
    )
}
